import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  Popconfirm,
  Space,
  Tag,
  Typography
} from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  applicationsApplicationsWebhookConfigClearSecretCreateMutation,
  applicationsApplicationsWebhookConfigPartialUpdateMutation,
  applicationsApplicationsWebhookConfigRetrieveOptions,
  applicationsApplicationsWebhookConfigRetrieveQueryKey,
  applicationsApplicationsWebhookConfigRotateSecretCreateMutation
} from 'src/api/main'
import { MonoValue } from 'src/components'
import { useShowApiErrors } from 'src/utils'

type Props = {
  applicationId: number
}

type FormValues = {
  webhook_endpoint: string
  public_access_webhook_endpoint: string
}

const trimToNull = (value: string | undefined | null): string | null => {
  if (value === undefined || value === null) return null
  const trimmed = value.trim()

  return trimmed.length > 0 ? trimmed : null
}

export const AppWebhooksPanel = ({ applicationId }: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const showApiErrors = useShowApiErrors()
  const [form] = Form.useForm<FormValues>()
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null)

  const enabled = applicationId > 0

  const { data: config, isLoading } = useQuery({
    ...applicationsApplicationsWebhookConfigRetrieveOptions({
      path: { id: applicationId }
    }),
    enabled
  })

  useEffect(() => {
    if (!config) return
    form.setFieldsValue({
      webhook_endpoint: config.webhook_endpoint ?? '',
      public_access_webhook_endpoint:
        config.public_access_webhook_endpoint ?? ''
    })
  }, [config, form])

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: applicationsApplicationsWebhookConfigRetrieveQueryKey({
        path: { id: applicationId }
      })
    })

  const { mutate: saveConfig, isPending: isSaving } = useMutation({
    ...applicationsApplicationsWebhookConfigPartialUpdateMutation(),
    onSuccess: async () => {
      message.success(
        t('workspace.apps.webhooks.messages.saved', 'Webhook settings saved')
      )
      await invalidate()
    },
    onError: (error) => showApiErrors(error, form)
  })

  const { mutate: rotateSecret, isPending: isRotating } = useMutation({
    ...applicationsApplicationsWebhookConfigRotateSecretCreateMutation(),
    onSuccess: async (data) => {
      setRevealedSecret(data.webhook_secret_token)
      await invalidate()
    },
    onError: (error) => showApiErrors(error)
  })

  const { mutate: clearSecret, isPending: isClearing } = useMutation({
    ...applicationsApplicationsWebhookConfigClearSecretCreateMutation(),
    onSuccess: async () => {
      setRevealedSecret(null)
      message.success(
        t(
          'workspace.apps.webhooks.messages.secretCleared',
          'Per-app secret removed'
        )
      )
      await invalidate()
    },
    onError: (error) => showApiErrors(error)
  })

  const renderSecretSection = () => {
    if (!config) return null

    if (config.has_webhook_secret_token) {
      return (
        <Space vertical size={8} style={{ width: '100%' }}>
          <Space size={8} wrap>
            <Tag color="success">
              {t(
                'workspace.apps.webhooks.secret.perAppActive',
                'Per-app secret set'
              )}
            </Tag>
            {config.webhook_secret_token_hint && (
              <Typography.Text type="secondary">
                {t('workspace.apps.webhooks.secret.hint', 'Hint')}:{' '}
                <Typography.Text code>
                  {config.webhook_secret_token_hint}
                </Typography.Text>
              </Typography.Text>
            )}
          </Space>
          <Space wrap>
            <Button
              size="small"
              onClick={() => rotateSecret({ path: { id: applicationId } })}
              loading={isRotating}
            >
              {t('workspace.apps.webhooks.secret.rotate', 'Rotate secret')}
            </Button>
            <Popconfirm
              title={t(
                'workspace.apps.webhooks.secret.clearConfirm',
                'Remove the per-app override and fall back to the template secret?'
              )}
              okText={t('confirm')}
              cancelText={t('cancel')}
              onConfirm={() => clearSecret({ path: { id: applicationId } })}
            >
              <Button size="small" danger loading={isClearing}>
                {t(
                  'workspace.apps.webhooks.secret.clear',
                  'Clear per-app secret'
                )}
              </Button>
            </Popconfirm>
          </Space>
        </Space>
      )
    }

    return (
      <Space vertical size={8} style={{ width: '100%' }}>
        <Space size={8} wrap>
          <Tag
            color={
              config.template_has_webhook_secret_token ? 'blue' : 'default'
            }
          >
            {config.template_has_webhook_secret_token
              ? t(
                  'workspace.apps.webhooks.secret.usingTemplate',
                  'Using template secret'
                )
              : t(
                  'workspace.apps.webhooks.secret.none',
                  'No secret configured'
                )}
          </Tag>
        </Space>
        <Button
          size="small"
          type="primary"
          onClick={() => rotateSecret({ path: { id: applicationId } })}
          loading={isRotating}
        >
          {t(
            'workspace.apps.webhooks.secret.generate',
            'Generate per-app secret'
          )}
        </Button>
      </Space>
    )
  }

  return (
    <Flex vertical gap={14}>
      {revealedSecret && (
        <Alert
          type="warning"
          showIcon
          closable={{ onClose: () => setRevealedSecret(null) }}
          title={t(
            'workspace.apps.webhooks.secret.revealedTitle',
            'New webhook secret'
          )}
          description={
            <Space vertical size={8}>
              <Typography.Text>
                {t(
                  'workspace.apps.webhooks.secret.revealedHint',
                  'Copy this secret now — it will not be shown again. It is sent in the Secret-token header on every webhook delivery.'
                )}
              </Typography.Text>
              <Typography.Paragraph copyable style={{ marginBottom: 0 }}>
                {revealedSecret}
              </Typography.Paragraph>
            </Space>
          }
        />
      )}

      <Card
        size="small"
        variant="outlined"
        title={t(
          'workspace.apps.webhooks.endpoints.title',
          'Webhook endpoints'
        )}
        loading={isLoading && !config}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) =>
            saveConfig({
              path: { id: applicationId },
              body: {
                webhook_endpoint: trimToNull(values.webhook_endpoint),
                public_access_webhook_endpoint: trimToNull(
                  values.public_access_webhook_endpoint
                )
              }
            })
          }
          initialValues={{
            webhook_endpoint: '',
            public_access_webhook_endpoint: ''
          }}
        >
          <Form.Item
            label={t(
              'workspace.apps.webhooks.endpoints.webhookEndpoint',
              'Webhook endpoint'
            )}
            name="webhook_endpoint"
            extra={
              config?.template_webhook_endpoint
                ? t(
                    'workspace.apps.webhooks.endpoints.inheritsHint',
                    'Inherits "{{value}}" from template. Leave empty to use the template default.',
                    { value: config.template_webhook_endpoint }
                  )
                : t(
                    'workspace.apps.webhooks.endpoints.appendedHint',
                    'Path appended to {{domain}}.',
                    { domain: config?.domain ?? '—' }
                  )
            }
          >
            <Input
              placeholder={
                config?.template_webhook_endpoint ?? '/webhooks/handler/'
              }
              allowClear
            />
          </Form.Item>

          <Form.Item
            label={t(
              'workspace.apps.webhooks.endpoints.publicAccessEndpoint',
              'Public-access webhook endpoint'
            )}
            name="public_access_webhook_endpoint"
            extra={
              config?.template_public_access_webhook_endpoint
                ? t(
                    'workspace.apps.webhooks.endpoints.inheritsHint',
                    'Inherits "{{value}}" from template. Leave empty to use the template default.',
                    {
                      value: config.template_public_access_webhook_endpoint
                    }
                  )
                : t(
                    'workspace.apps.webhooks.endpoints.publicAccessHint',
                    'Receives public-access credential lifecycle events.'
                  )
            }
          >
            <Input
              placeholder={
                config?.template_public_access_webhook_endpoint ??
                '/webhooks/public-access/'
              }
              allowClear
            />
          </Form.Item>

          <Flex justify="flex-end">
            <Button
              size="small"
              type="primary"
              htmlType="submit"
              loading={isSaving}
            >
              {t('button.save', 'Save')}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card
        size="small"
        variant="outlined"
        title={t('workspace.apps.webhooks.secret.title', 'Webhook secret')}
        loading={isLoading && !config}
      >
        <Flex vertical gap={12}>
          <Typography.Text type="secondary">
            {t(
              'workspace.apps.webhooks.secret.description',
              'Sent as the Secret-token header on each webhook delivery so the receiver can verify the request.'
            )}
          </Typography.Text>
          {renderSecretSection()}
        </Flex>
      </Card>

      {config && (
        <Card
          size="small"
          variant="outlined"
          title={t(
            'workspace.apps.webhooks.delivery.title',
            'Delivery & reliability'
          )}
        >
          <Descriptions
            column={{ xs: 1, sm: 2 }}
            size="small"
            colon={false}
            styles={{ label: { width: 220 } }}
          >
            <Descriptions.Item
              label={t(
                'workspace.apps.webhooks.delivery.effectiveUrl',
                'Effective webhook URL'
              )}
            >
              {config.effective_webhook_url ? (
                <MonoValue ellipsis>{config.effective_webhook_url}</MonoValue>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t(
                'workspace.apps.webhooks.delivery.effectivePublicAccessUrl',
                'Effective public-access URL'
              )}
            >
              {config.effective_public_access_webhook_url ? (
                <MonoValue ellipsis>
                  {config.effective_public_access_webhook_url}
                </MonoValue>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t(
                'workspace.apps.webhooks.delivery.maxRetries',
                'Max retries'
              )}
            >
              {config.delivery_max_retries}
            </Descriptions.Item>
            <Descriptions.Item
              label={t(
                'workspace.apps.webhooks.delivery.timeout',
                'Request timeout'
              )}
            >
              {config.delivery_timeout_seconds}s
            </Descriptions.Item>
            <Descriptions.Item
              label={t(
                'workspace.apps.webhooks.delivery.retryMaxDelay',
                'Max retry delay'
              )}
            >
              {config.delivery_retry_max_delay_seconds}s
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    </Flex>
  )
}
